import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { imgUrl } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1627861446476-42fd9925e151?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxwaWNrbGUlMjBqYXIlMjBob21lbWFkZXxlbnwwfHx8fDE3ODcxNTYzMzB8MA&ixlib=rb-4.1.0&q=85";

const DEFAULT_HERO = {
  eyebrow: "35.6923° N, 97.0645° W — Wellston, Oklahoma",
  line1: "Unapologetically",
  line2: "Crisp.",
  line3: "Small-batch pickled on Route 66.",
  subtext:
    "Hand-cut heirloom cucumbers, wild Oklahoma garlic, whole dill heads, and slow oak-cask vinegar brines. Never pasteurized into mush — pickled right here in Wellston, OK.",
  cta_primary: "Shop the Brine",
  cta_secondary: "Our Wellston Roots",
  footnote: "Free pickup at the farmstand • Local delivery across Lincoln County",
  image: HERO_IMG,
  stamp_center: "PMOK!",
  stamp_ring: "EST. WELLSTON OK • ROUTE 66 • SMALL BATCH •",
};

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroLogo = () => (
  <motion.div
    initial={{ scale: 0, rotate: -14 }}
    animate={{ scale: 1, rotate: -4 }}
    transition={{ delay: 1.1, type: "spring", stiffness: 180, damping: 13 }}
    className="absolute -bottom-10 -left-10 w-36 h-36 sm:w-44 sm:h-44"
    data-testid="hero-stamp-badge"
  >
    <motion.img
      src="/logo.png"
      alt="Pickle Me OK! stamp logo"
      className="w-full h-full rounded-full object-cover shadow-2xl ring-4 ring-parchment"
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
    />
  </motion.div>
);

export const Hero = ({ content }) => {
  const c = { ...DEFAULT_HERO, ...(content || {}) };
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative overflow-hidden jar-spotlight" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 sm:pt-14 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <motion.div style={{ opacity: fade }} className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-terra font-semibold mb-5"
              data-testid="hero-eyebrow"
            >
              {c.eyebrow}
            </motion.p>

            <h1 className="font-serif font-black tracking-tight leading-[1.05] text-dill text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>{c.line1}</MaskedLine>
              <MaskedLine delay={0.3} className="italic text-terra">
                {c.line2}
              </MaskedLine>
              <MaskedLine delay={0.45} className="text-xl sm:text-2xl lg:text-3xl font-bold text-ink/75 mt-2">
                {c.line3}
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8 }}
              className="mt-5 max-w-lg text-sm sm:text-base text-ink/65 leading-relaxed"
              data-testid="hero-subtagline"
            >
              {c.subtext}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-7 flex flex-wrap items-center gap-3"
            >
              <button
                onClick={() => scrollTo("shop")}
                className="group bg-dill hover:bg-terra text-parchment font-semibold rounded-full px-6 py-3 text-sm transition-colors duration-300 flex items-center gap-2"
                data-testid="hero-shop-button"
              >
                {c.cta_primary}
                <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              </button>
              <button
                onClick={() => scrollTo("story")}
                className="border-2 border-dill/25 hover:border-terra hover:text-terra text-dill font-semibold rounded-full px-6 py-[10px] text-sm transition-colors duration-300"
                data-testid="hero-story-button"
              >
                {c.cta_secondary}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 1 }}
              className="mt-7 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45"
            >
              <MapPin className="w-3.5 h-3.5 text-terra" />
              {c.footnote}
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, rotate: 3 }}
              animate={{ opacity: 1, scale: 1, rotate: 2 }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="relative">
                <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-2xl bg-gold/30" aria-hidden="true" />
                <img
                  src={imgUrl(c.image)}
                  alt="Hand holding a Pickle Me OK! jar of chilled pickles in front of a stained-glass tower"
                  className="relative w-full aspect-square object-cover object-[center_80%] rounded-2xl shadow-xl border-4 border-parchment"
                  data-testid="hero-image"
                />
              </motion.div>
              <HeroLogo />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
